import React, { ChangeEvent, Fragment, ReactElement, useState } from "react";
import { IMillProductDetail } from "../types";
import {
  CircularProgress,
  Paper,
  Table, TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel, Tooltip,
  TablePagination,
} from "@mui/material";
import {
  formatDateTimeWithSeconds,
  getComparator, saveBlobAs,
  stableSort, toTons
} from "../../../system/utils";
import { BaseType, IColumn, IParentSize, IState, Order } from "../../../types";
import StyledTableRow from "../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import TimelineIcon from "@mui/icons-material/Timeline";
import Detail from "../../../components/dialogs/detail";
import ProductTrends from "../components/dialogPanels/productTrends";
import TableContainer from "../../../components/tableContainer";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { getPalette } from "../../../configuration/configuration";
import TablePaginationActions from "../../../components/tablePaginationActions";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSelector } from "react-redux";

interface IProductDetailProps {
  products: IMillProductDetail[] | null;
  parentSize: IParentSize;
}

interface IDetailDialog {
  open: boolean;
  item: IMillProductDetail | null;
}

const columns: IColumn[] = [
  {
    id: "actions",
    label: "",
    minWidth: 20
  },
  {
    id: "status",
    label: "",
    minWidth: 20
  },
  {
    id: "strand",
    label: "Strand",
    minWidth: 80,
  },
  {
    id: "millHeat",
    label: "Mill Heat",
    minWidth: 100,
  },
  {
    id: "meltHeat",
    label: "Heat Number",
    minWidth: 90,
  },
  {
    id: "grade",
    label: "Grade",
    minWidth: 90,
  },
  {
    id: "recipe",
    label: "Recipe",
    minWidth: 90,
  },
  {
    id: "billet",
    label: "Billet",
    minWidth: 50,
    align: "right",
  },
  {
    id: "weight",
    label: <span>Weight <sub>[US ton]</sub></span>,
    align: "right",
    minWidth: 90,
    format: value => toTons(value).toFixed(2)
  },
  {
    id: "compactingLength",
    label: <span>Compacting Length <sub>[mm]</sub></span>,
    align: "right",
    minWidth: 90
  },
  {
    id: "compactingPressure",
    label: <span>Compacting Pressure <sub>[]</sub></span>,
    align: "right",
    minWidth: 90
  },
  {
    id: "pressing",
    label: "Pressing",
    align: "center",
    minWidth: 90
  },
  {
    id: "beginDatetime",
    label: "Begin Datetime",
    minWidth: 150,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "endDatetime",
    label: "End Datetime",
    minWidth: 150,
    format: value => formatDateTimeWithSeconds(value),
  }
];


const ProductionTableDetail = ({ products, parentSize }: IProductDetailProps): ReactElement => {
  const filter = useSelector((state: IState) => state.filter);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IMillProductDetail>("beginDatetime");
  const [openDetail, setOpenDetail] = useState<IDetailDialog>({ open: false, item: null });
  const [selectedItem, seSelectedItem] = useState<IMillProductDetail | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableContainerHeight = parentSize.height - 50;

  const dataLength = products ? products.length : 0;

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;

  const createSortHandler = (property: keyof IMillProductDetail) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const onShowCharts = (item: IMillProductDetail) => {
    setOpenDetail({ open: true, item });
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onExportData = () => {
    let fileContent = "";

    // fileContent += `FROM;${filter.local.selectedFrom.toFormat("MM-dd-yyyy")}\n`;
    // fileContent += `TO;${filter.local.selectedTo.toFormat("MM-dd-yyyy")}\n`;
    // fileContent += `Shift;${filter.local.shift}\n`;
    // fileContent += `Crew;${filter.local.crew}\n`;
    // fileContent += "\n";
    fileContent += "Strand;Status;Mill Heat;Heat Number;Steel Grade;Recipe;Billet;Weight[US ton];Compacting Length[mm];Compacting Pressure;Pressing;Begin Datetime;End Datetime;\n";

    products?.forEach(row => {
      let status = "Pending";
      if (row.isCutUp) {
        status = "Cut up";
      } else if (row.isCobble) {
        status = "Cobble";
      } else if (row.weight) {
        status = "Processed";
      }

      let pressing = "---";
      if (row.pressing === 0) {
        pressing = "x1";
      } else if (row.pressing === 1) {
        pressing = "x2";
      }


      fileContent += `${row.machine.name};${status};${row.millHeat};${row.meltHeat};${row.grade};${row.recipe ?? "---"};${row.billet};`;
      fileContent += `${row.weight ? toTons(row.weight).toFixed(2) : ""};${row.compactingLength ?? ""};`;
      fileContent += `${row.compactingPressure ?? ""};${pressing};`;
      fileContent += `${formatDateTimeWithSeconds(row.beginDatetime)};${formatDateTimeWithSeconds(row.endDatetime)};\n`;
    });

    const file = new Blob([ fileContent ], { type: "text/csv" });

    saveBlobAs(URL.createObjectURL(file), `mr-billets-${filter.local.selectedFrom}-${filter.local.selectedTo}.csv`);
  };

  // rowsPerPage > 0
  //             ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  //             : rows

  return (
    <>
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              {columns.map((column) =>
                <TableCell
                  className="table-header"
                  key={column.id}
                  align={column.align}
                  sortDirection={orderBy === column.id ? order : false}>
                  {column.id !== "actions"
                    ? <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as keyof IMillProductDetail)}>
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>
                    : <Fragment/>}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {products && stableSort<IMillProductDetail>(products, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: IMillProductDetail, index: number) => {
                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}
                    onClick={() => seSelectedItem(row)} selected={row === selectedItem}>
                    {columns.map((column: IColumn) => {
                      if (column.id === "actions") {
                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            <IconButton aria-label="edit" size={"small"}
                              onClick={() => onShowCharts(row as IMillProductDetail)}>
                              <TimelineIcon/>
                            </IconButton>
                          </TableCell>
                        );
                      }

                      if (column.id === "strand") {
                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            {row.machine.name}
                          </TableCell>);
                      }

                      if (column.id === "status") {
                        let status: number;
                        if (row.isCobble || row.isCutUp) {
                          status = 2;
                        } else {
                          status = row.weight ? 1 : 0;
                        }

                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            {status === 0 &&
                              <Tooltip title="Pending">
                                <ErrorOutlineIcon style={{
                                  color: getPalette().statusColor.info.background,
                                  verticalAlign: "center",
                                  marginLeft: "0.5em"
                                }}/>
                              </Tooltip>}
                            {status === 1 &&
                              <Tooltip title="Processed">
                                <CheckCircleOutlineIcon style={{
                                  color: getPalette().statusColor.good.background,
                                  verticalAlign: "center",
                                  marginLeft: "0.5em"
                                }}/>
                              </Tooltip>
                            }
                            {status === 2 &&
                              <Tooltip title={`Rejected:${row.isCobble ? " Cobble" : ""} ${row.isCutUp ? "Cut up" : ""}`}>
                                <HighlightOffIcon style={{
                                  color: getPalette().statusColor.error.background,
                                  verticalAlign: "center",
                                  marginLeft: "0.5em"
                                }}/>
                              </Tooltip>
                            }
                          </TableCell>
                        );
                      }

                      if (column.id === "pressing") {
                        return row.pressing === null
                          ? <TableCell key={column.id} align={column.align} className="table-cell">
                              ---
                          </TableCell>
                          : <TableCell key={column.id} align={column.align} className="table-cell">
                            { row.pressing === 1 ? "x2" : "x1"}
                          </TableCell>;
                      }


                      const value = row[column.id as keyof IMillProductDetail] as BaseType;

                      return (
                        <TableCell key={column.id} align={column.align} className="table-cell">
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}

            {emptyRows > 0 &&
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}/>
              </TableRow>
            }
          </TableBody>
        </Table>

        {!products &&
          <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2">
            <CircularProgress className=""/>
          </Paper>
        }
        {openDetail.open && openDetail.item &&
          <Detail closed={() => setOpenDetail({ open: false, item: null })} fullWidth={true} maxWidth={"lg"}
            title={`Charts - Mill Order ${openDetail.item.millHeat} - ${openDetail.item.machine.name} - Billet ${openDetail.item.billet}`}>
            <ProductTrends productDetail={openDetail.item}/>
          </Detail>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <IconButton color="primary" onClick={onExportData} disabled={!products}>
                <Tooltip title={"Export data"}>
                  <FileDownloadIcon/>
                </Tooltip>
              </IconButton>
            </TableCell>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ProductionTableDetail;
